import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { format } from 'date-fns';
import { map } from 'rxjs/operators';

/**
 * ISO 8601 datetime pattern - matches formats like:
 * - "2026-01-26T12:14:35.2063701"
 * - "2026-01-26T12:14:35.206Z"
 * - "2026-01-26T12:14:35+05:00"
 *
 * Does NOT match date-only strings like "2026-01-26"
 */
const isoDateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;

/**
 * Date-only fields serialized by the backend as DateTime at midnight with no timezone
 * (e.g. "2026-04-18T00:00:00"). These are calendar dates, not instants — shifting them
 * to local time makes users west of UTC see the previous day.
 */
const dateOnlyAtMidnightPattern = /^\d{4}-\d{2}-\d{2}T00:00:00$/;

/**
 * URLs that should be excluded from date transformation
 * (e.g., S3 URLs, external APIs)
 */
const excludedUrlPatterns = ['s3.ca-central-1', 's3.amazonaws.com'];

/**
 * HTTP interceptor that automatically converts UTC datetime strings
 * in API responses to local timezone.
 *
 * Features:
 * - Recursively transforms ISO datetime strings to local timezone
 * - Wrapped in try-catch to prevent crashes on malformed data
 * - Skips Blob/ArrayBuffer responses
 * - Excludes S3 URLs from transformation
 * - Only transforms datetime strings (with 'T'), not date-only strings
 */
export const dateTransformInterceptor: HttpInterceptorFn = (req, next) => {
  return next(req).pipe(
    map(event => {
      if (event instanceof HttpResponse && event.body) {
        // Skip transformation for excluded URLs
        if (excludedUrlPatterns.some(pattern => req.url.includes(pattern))) {
          return event;
        }

        // Skip transformation for blob/arraybuffer responses
        if (event.body instanceof Blob || event.body instanceof ArrayBuffer) {
          return event;
        }

        // Wrap transformation in try-catch to prevent crashes
        try {
          const transformedBody = transformDates(event.body);
          return event.clone({ body: transformedBody });
        } catch (error) {
          console.error('Date transformation failed:', error);
          return event; // Return original response on failure
        }
      }
      return event;
    })
  );
};

/**
 * Recursively traverse the object and transform ISO datetime strings to local timezone
 */
function transformDates(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Pass through Blob, ArrayBuffer, and other binary types unchanged
  if (obj instanceof Blob || obj instanceof ArrayBuffer || obj instanceof Uint8Array) {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => transformDates(item));
  }

  // Handle Date objects (pass through)
  if (obj instanceof Date) {
    return obj;
  }

  // Handle plain objects only (not class instances)
  if (typeof obj === 'object' && obj.constructor === Object) {
    const transformed: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
      transformed[key] = transformDates((obj as Record<string, unknown>)[key]);
    }
    return transformed;
  }

  // Handle strings - check if it's an ISO datetime string
  if (typeof obj === 'string' && isIsoDateTimeString(obj)) {
    return convertToLocalTimeString(obj);
  }

  // Return everything else unchanged (numbers, booleans, class instances, etc.)
  return obj;
}

/**
 * Check if a string matches ISO 8601 datetime format
 */
function isIsoDateTimeString(value: string): boolean {
  return isoDateTimePattern.test(value);
}

/**
 * Convert UTC datetime string to local timezone ISO string
 *
 * Input:  "2026-01-26T12:14:35.2063701" (UTC)
 * Output: "2026-01-26T17:14:35.206+05:00" (Local timezone, e.g., PKT UTC+5)
 */
function convertToLocalTimeString(utcDateString: string): string {
  // Date-only fields come as "YYYY-MM-DDT00:00:00" — pass through untouched so
  // negative-UTC-offset users don't see the previous day.
  if (dateOnlyAtMidnightPattern.test(utcDateString)) {
    return utcDateString;
  }

  // If the string doesn't end with 'Z' or timezone offset, treat it as UTC
  let dateStr = utcDateString;
  if (!utcDateString.endsWith('Z') && !utcDateString.match(/[+-]\d{2}:\d{2}$/)) {
    dateStr = utcDateString + 'Z'; // Append Z to indicate UTC
  }

  const date = new Date(dateStr);

  // Check for invalid date
  if (isNaN(date.getTime())) {
    return utcDateString; // Return original if parsing fails
  }

  // Return ISO string in local timezone format
  return toLocalISOString(date);
}

/**
 * Convert Date to ISO string in local timezone
 * JavaScript's toISOString() always returns UTC, so we format with the local offset.
 * Output e.g. "2026-01-26T17:14:35.206+05:00"
 */
function toLocalISOString(date: Date): string {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
}
